import React from "react";
import {
  DropLocationCaptureProperty,
  DropOverride,
  ELightPath,
  FluorescenceCaptureProperty,
  PlateTypeCaptureProfile,
  UltravioletCaptureProperty,
} from "../../../Model/CaptureProfile";
import EOpticAndCameraFeature from "../../../Model/EOpticAndCameraFeature";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import { PlateType } from "../../../Model/PlateType";
import DefaultImagingSettingComboBox from "./DefaultImagingSettingComboBox";
import DefaultImagingSettingRadioButton from "./DefaultImagingSettingRadioButton";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import ObjectiveZoomDropOverride from "./ObjectiveZoomDropOverride";
import RoiOptions from "./RoiOptions";
import ImagerUtil from "../../../Utility/ImagerUtil";

const MISSED_DROP_ZOOM = "missedDropZoom";
const DROP_LOCATION_METHOD = "dropLocationMethod";

const getDefaultDropOptionOverrides = (
  numberOfDrop: number,
  minContinousZoom = 1,
  minObjectiveZoom = 1,
  minCropZoom = 1
): DropOverride[] => {
  let newDropOverrides = [] as DropOverride[];

  for (let i = 0; i < numberOfDrop; i++) {
    newDropOverrides.push({
      continuousZoom: minContinousZoom,
      objectiveZoom: minObjectiveZoom,
      cropZoom: minCropZoom,
      roi: {
        autoSelectObjective: true,
        fixedObjective: minObjectiveZoom,
      },
    });
  }

  return newDropOverrides;
};

type Props = {
  style?: React.CSSProperties;
  lightPath: ELightPath;
  lightpathCapability: LightPathCapability;
  visibleCaptureProfile: PlateTypeCaptureProfile;
  captureProfile: PlateTypeCaptureProfile;
  plateType: PlateType;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
  setDisableAllMenu: (disable: boolean) => void;
};

function GetLightpathCaptureProperty(
  captureProfile: PlateTypeCaptureProfile,
  lightPath: ELightPath
): UltravioletCaptureProperty | FluorescenceCaptureProperty {
  switch (lightPath) {
    case ELightPath.Fluorescence:
      if (captureProfile.fluorescence == null) {
        throw new Error("Fluorescence capture profile is null");
      }
      return captureProfile.fluorescence;
    case ELightPath.Ultraviolet:
      if (captureProfile.ultraviolet == null) {
        throw new Error("Ultraviolet capture profile is null");
      }
      return captureProfile.ultraviolet;
    default:
      if (captureProfile.visible == null) {
        throw new Error("Visible capture profile is null");
      }
      return captureProfile.visible;
  }
}

export default function NonVisibleDropLocation({
  style = {},
  lightpathCapability,
  lightPath,
  visibleCaptureProfile,
  captureProfile,
  setCaptureProfile,
  plateType,
  onErrorOccur,
  onErrorCleared,
  setDisableAllMenu,
}: Props) {
  const lightpathCaptureProperty = GetLightpathCaptureProperty(
    captureProfile,
    lightPath
  );

  if (visibleCaptureProfile.dropLocation == null) {
    throw new Error("Drop location is null");
  }
  if (captureProfile.dropLocation == null) {
    throw new Error("Drop location is null");
  }

  const useDropLocation =
    visibleCaptureProfile.dropLocation.dropLocationMethod !== 0;
  const dropLocationCaptureProperty = captureProfile.dropLocation;
  const numberOfDropPerWell = plateType.numberOfDropPerWell;

  const onDropLocationCapturePropertyChanged = (
    dropLocation: DropLocationCaptureProperty
  ): void => {
    setCaptureProfile({
      ...captureProfile,
      dropLocation: dropLocation,
    });
  };

  const onLightpathCapturePropertyChanged = (
    lightpathCaptureProperty:
      | UltravioletCaptureProperty
      | FluorescenceCaptureProperty
  ): void => {
    setCaptureProfile({
      ...captureProfile,
      ultraviolet: lightpathCaptureProperty as UltravioletCaptureProperty,
      fluorescence: lightpathCaptureProperty as FluorescenceCaptureProperty,
    });
  };

  const objectiveZoom = lightpathCapability.optic.objectiveZoom;
  const cropZoom = lightpathCapability.optic.cropZoom;
  let objectiveZoomView = null;
  let missedObjectiveZoomView = null;
  let cropZoomView = null;
  let dropOptionOverrideView = null;

  const dropOptionOverrides =
    captureProfile.dropOverrides !== undefined &&
    captureProfile.dropOverrides !== null &&
    captureProfile.dropOverrides?.length > 0
      ? captureProfile.dropOverrides
      : getDefaultDropOptionOverrides(
          numberOfDropPerWell,
          1,
          objectiveZoom?.objectives[0].value,
          cropZoom?.minCropZoom
        );
  if (objectiveZoom) {
    const displayvalues =
      ImagerUtil.getObjectiveZoomDisplayValues(objectiveZoom);

    if (lightpathCaptureProperty.objectiveZoom == null) {
      throw new Error("Capture profile objective zoom is null");
    }

    objectiveZoomView = (
      <DefaultImagingSettingComboBox
        displayValues={displayvalues}
        value={lightpathCaptureProperty.objectiveZoom.toFixed(1)}
        onChange={(name, value) =>
          onLightpathCapturePropertyChanged({
            ...lightpathCaptureProperty,
            objectiveZoom: Number.parseFloat(value),
          })
        }
        label="Objective"
        name={EOpticAndCameraFeature.ObjectiveZoom}
        unit=""
        disabled={
          useDropLocation ||
          captureProfile.dropLocation?.dropOptionOverrideExist
        }
        style={{
          paddingLeft: "13px",
        }}
      />
    );

    missedObjectiveZoomView = (
      <DefaultImagingSettingComboBox
        displayValues={displayvalues}
        value={captureProfile.dropLocation.missedDropZoom.toString()}
        onChange={(name, value) =>
          onDropLocationCapturePropertyChanged({
            ...dropLocationCaptureProperty,
            missedDropZoom: Number.parseFloat(value),
          })
        }
        label="Missed Objective"
        name={MISSED_DROP_ZOOM}
        unit="x"
        disabled={!useDropLocation}
        style={{
          paddingLeft: "13px",
        }}
      />
    );
    if (plateType.numberOfDropPerWell > 1) {
      dropOptionOverrideView = (
        <ObjectiveZoomDropOverride
          captureProfile={captureProfile}
          setCaptureProfile={setCaptureProfile}
          plateType={plateType}
          onErrorOccur={onErrorOccur}
          onErrorCleared={onErrorCleared}
          dropOptionOverrides={dropOptionOverrides}
          objectiveZoom={objectiveZoom}
          cropZoom={cropZoom}
          disabled={useDropLocation}
          lightPath={ELightPath.Visible}
          lightPathCapability={lightpathCapability}
        />
      );
    }
  }

  if (cropZoom) {
    if (lightpathCaptureProperty.cropZoom == null) {
      throw new Error("Capture profile Crop zoom is null");
    }

    cropZoomView = (
      <DefaultImagingSettingTextBox
        value={lightpathCaptureProperty.cropZoom}
        onChange={(name, value) =>
          onLightpathCapturePropertyChanged({
            ...lightpathCaptureProperty,
            cropZoom: value,
          })
        }
        label="Zoom"
        name={EOpticAndCameraFeature.ContinuousZoom}
        unit="x"
        min={cropZoom.minCropZoom}
        max={cropZoom.maxCropZoom}
        disabled={
          useDropLocation ||
          captureProfile.dropLocation?.dropOptionOverrideExist
        }
        style={{
          paddingLeft: "13px",
          marginTop: 5,
          marginBottom: 5,
        }}
        isInteger={false}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />
    );
  }

  const locateDropMethodDisplayValues = [
    {
      value: "1",
      display: "Standard",
    },
    {
      value: "2",
      display: "Lipidic Cubic Phase",
    },
  ];

  return (
    <div
      style={{
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        ...style,
      }}
    >
      {/* //-------DROP_LOCATE_METHOD_SELECTION-------// */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "baseline",
          width: "100%",
        }}
      >
        <label
          style={{
            fontWeight: "bold",
            height: "100%",
          }}
        >
          Drop Location
        </label>
        <DefaultImagingSettingRadioButton
          name="dropLocationMethod"
          label="Fixed"
          checked={visibleCaptureProfile.dropLocation.dropLocationMethod === 0}
          onChange={(name, value) => {}}
          style={{
            paddingLeft: "13px",
          }}
          disabled={true}
        />

        <DefaultImagingSettingRadioButton
          name="dropLocationMethod"
          label="Locate Drop"
          checked={visibleCaptureProfile.dropLocation.dropLocationMethod !== 0}
          onChange={(name, value) => {}}
          style={{
            paddingLeft: "13px",
          }}
          disabled={true}
        />
      </div>

      {/* //-------FIXED_DROP_LOCATE_METHOD_ZOOM-------// */}

      <div style={{ margin: "24px 0px", fontWeight: "bold" }}>
        Fixed Method Settings
      </div>

      {objectiveZoomView}
      {cropZoomView}
      {dropOptionOverrideView}

      {/* //-------LOCATE_DROP_METHOD_SETTING-------// */}

      <div style={{ margin: "24px 0px", fontWeight: "bold" }}>
        Locate Drop Method Settings
      </div>

      <DefaultImagingSettingComboBox
        displayValues={locateDropMethodDisplayValues}
        value={visibleCaptureProfile.dropLocation.dropLocationMethod.toString()}
        onChange={() => {}}
        label="Method"
        name={DROP_LOCATION_METHOD}
        unit=""
        disabled={true}
        style={{
          paddingLeft: "13px",
          marginBottom: 5,
        }}
      />

      {missedObjectiveZoomView}

      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateAreas: `'gamma polarizer ' 
                              'condenser advancedButton'`,
          gridTemplateColumns: "50% 50%",
          gridTemplateRows: "auto auto",
          gridRowGap: "5px",
          marginLeft: "0px",
          marginTop: "5px",
          paddingLeft: "13px",
        }}
      >
        {objectiveZoom && (
          <RoiOptions
            disabled={captureProfile.dropLocation.dropOptionOverrideExist}
            roi={captureProfile.roi}
            onValueChanged={(roi) =>
              setCaptureProfile({ ...captureProfile, roi: roi })
            }
            lightPath={lightPath}
            lightpathCapability={lightpathCapability}
            fixedObjectiveDropDownDirection="up"
          />
        )}
      </div>
    </div>
  );
}
